import useSQLite from '../useSQLite'
import { SQLITE_FEATURES } from './registry'

const { dbRun, dbQuery, dbExecute, pruneKeys, getKeyStats, listEntries } = useSQLite()

const METHOD_NAMES = {
    onBoot: 'boot',
    getSummary: 'summary',
    purge: 'purge',
}

const getFeatureByKey = (key) => SQLITE_FEATURES.find((feature) => feature.key === key) || null

const createFeatureContext = () => ({
    dbRun,
    dbQuery,
    dbExecute,
    pruneKeys,
    getKeyStats,
    listEntries,
})

const resolveFallback = (feature, method) => {
    const fallback = feature?.fallbacks?.[method]
    if (fallback !== undefined) return fallback
    if (method === 'getSummary') return null
    return { ok: false }
}

const shouldPurgeFeatureOnLogout = (feature) => feature?.purgeOnLogout === true

const executeFeatureMethod = async (feature, method, ctx) => {
    await feature.ensureSchema(ctx)
    return feature[method](ctx)
}

const shouldRetryBootOnce = (method, error) => {
    if (method !== 'onBoot') return false
    const message = String(error?.message || error || '').toLowerCase()
    return message.includes('connection app_data does not exist')
}

const runFeatureMethod = async (feature, method, ctx) => {
    try {
        return await executeFeatureMethod(feature, method, ctx)
    } catch (error) {
        if (shouldRetryBootOnce(method, error)) {
            const methodLabel = METHOD_NAMES[method] || method
            console.info(`[SQLite][Feature:${feature.key}] ${methodLabel} missing connection, retrying once`)
            try {
                return await executeFeatureMethod(feature, method, ctx)
            } catch (retryError) {
                error = retryError
            }
        }

        const methodLabel = METHOD_NAMES[method] || method
        console.warn(`[SQLite][Feature:${feature.key}] ${methodLabel} failed`, error)
        return resolveFallback(feature, method)
    }
}

export async function runCacheCleanup() {
    const ctx = createFeatureContext()
    const results = {}
    for (const feature of SQLITE_FEATURES) {
        results[feature.key] = await runFeatureMethod(feature, 'onBoot', ctx)
    }
    return { ok: true, results }
}

export async function getCacheSummary() {
    const ctx = createFeatureContext()
    const summary = {}
    for (const feature of SQLITE_FEATURES) {
        summary[feature.key] = await runFeatureMethod(feature, 'getSummary', ctx)
    }
    return summary
}

export async function purgeAllCaches() {
    const ctx = createFeatureContext()
    const results = {}
    for (const feature of SQLITE_FEATURES) {
        results[feature.key] = await runFeatureMethod(feature, 'purge', ctx)
    }
    return { ok: true, results }
}

export async function purgeLogoutCaches() {
    const ctx = createFeatureContext()
    const results = {}
    for (const feature of SQLITE_FEATURES.filter(shouldPurgeFeatureOnLogout)) {
        results[feature.key] = await runFeatureMethod(feature, 'purge', ctx)
    }
    return { ok: true, results }
}

export async function runFeatureBoot(key) {
    const feature = getFeatureByKey(key)
    if (!feature) {
        throw new Error(`[SQLite][Feature] Unknown feature "${key}"`)
    }
    return runFeatureMethod(feature, 'onBoot', createFeatureContext())
}

export async function getFeatureSummary(key) {
    const feature = getFeatureByKey(key)
    if (!feature) {
        throw new Error(`[SQLite][Feature] Unknown feature "${key}"`)
    }
    return runFeatureMethod(feature, 'getSummary', createFeatureContext())
}

export async function purgeFeature(key) {
    const feature = getFeatureByKey(key)
    if (!feature) {
        throw new Error(`[SQLite][Feature] Unknown feature "${key}"`)
    }
    return runFeatureMethod(feature, 'purge', createFeatureContext())
}

export function listSqliteFeatureKeys() {
    return SQLITE_FEATURES.map((feature) => feature.key)
}
